import { Component, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { CommuniquesService } from '../communiques.service';

export interface Communique {
  id_press_release: number;
  title_ln1: string;
  publish_date: string;
  reference?: string;
  press_tags?: string;
  status?: 'ACTI' | 'INAC' | 'ARCH';
  status_label?: string;
  pdf_link?: string;
  pdf_name?: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

@Component({
  selector: 'app-communiques-list',
  standalone: false,
  templateUrl: './communiques-list.component.html',
  styleUrls: ['./communiques-list.component.scss']
})
export class CommuniquesListComponent implements OnInit, OnDestroy {
  communiques = signal<Communique[]>([]);
  isLoading = signal(true);
  isFiltering = signal(false);
  errorMessage = signal<string | null>(null);
  currentPage = signal(1);
  searchQuery = signal('');
  selectedStatus = signal('');
  totalCommuniques = signal(0);
  perPage = signal(8);
  pagination = signal<Pagination>({ page: 1, limit: 8, total: 0, totalPages: 0 });

  pageNumbers = computed((): (number | '...')[] => {
    const { page, totalPages } = this.pagination();
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  });

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(
    private communiquesService: CommuniquesService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(query => {
      this.searchQuery.set(query);
      this.loadCommuniques(1, true);
    });
    this.loadCommuniques(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  private loadCommuniques(page: number, filtering = false): void {
    if (filtering) {
      this.isFiltering.set(true);
    } else {
      this.isLoading.set(true);
    }
    this.errorMessage.set(null);

    this.communiquesService.getCommuniques(page, this.pagination().limit, this.searchQuery(), this.selectedStatus())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.communiques.set(response.data || []);
          const total = response.pagination?.total || response.total || 0;
          this.pagination.set({
            page,
            limit: this.pagination().limit,
            total,
            totalPages: Math.ceil(total / this.pagination().limit),
          });
          this.isLoading.set(false);
          this.isFiltering.set(false);
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message || 'Erreur lors du chargement des communiqués');
          this.isLoading.set(false);
          this.isFiltering.set(false);
        }
      });
  }

  onSearch(value: string): void {
    this.searchSubject.next(value);
  }

  onStatusFilter(value: string): void {
    this.selectedStatus.set(value);
    this.loadCommuniques(1, true);
  }

  onPageChange(page: number): void {
    const { totalPages } = this.pagination();
    if (page < 1 || page > totalPages) return;
    this.loadCommuniques(page);
  }

  onAddNew(): void {
    this.router.navigate(['/federation/communiques/new']);
  }

  onRowClick(id: number): void {
    this.router.navigate(['/federation/communiques', id]);
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = { ACTI: 'En ligne', INAC: 'Hors ligne', ARCH: 'Archivé' };
    return map[status] || status;
  }

  statusBadgeClass(status: string): string {
    if (status === 'ACTI') return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    if (status === 'ARCH') return 'bg-stone-100 text-stone-500 border-stone-200';
    return 'bg-amber-50 text-amber-600 border-amber-100';
  }
}
